import { HttpResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '../../../../environments/environment';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { IMPERSONATION_PROBE_TERM } from '../../impersonation/impersonation.constants';
import { ImpersonationConfirmDialogComponent } from '../../impersonation/impersonation-confirm-dialog/impersonation-confirm-dialog.component';
import { clearPossibleSubscriptions } from '../../../../../../../libs/pulpo-commons/src/lib/utils/subscription-helper';
import { PulpoSnackbarComponent } from '@pulpo/pulpo-commons';
import { ImpersonationService, UserService, UserGroupService } from '@pulpo/pulpo-api';
import { User, FullUser, UserGroup, isAgencyGroup } from '@pulpo/pulpo-models';
import dayjs from 'dayjs';

@Component({
  standalone: false,
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, AfterViewInit, OnDestroy {
  dataSource: MatTableDataSource<FullUser>;
  displayedColumns: string[];
  itemsPerPage: number;
  page: number;
  filter: any = {};
  loading = true;
  nbUsers: number;
  /**
   * Whether the connected user may open sessions as someone else (PUL-2840). The `admin`
   * permission on the producer group is not readable client-side, so the search endpoint is
   * used as the probe: any failure hides the feature.
   */
  canImpersonate = false;
  /**
   * Ids of the rows attached to an agency, so the template does not walk `groupDescriptions`
   * again on every change detection cycle. Rebuilt whenever the users or the agency groups
   * land — either request can answer first.
   */
  private impersonableUserIds = new Set<string>();
  expandedGroups: Map<string, Set<string>> = new Map(); // userId -> Set<groupName>
  agencyGroupsMap: Map<string, string> = new Map(); // groupName -> groupName for agency groups
  private userSubscription: Subscription;
  private userUpdateSubscription: Subscription;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  constructor(
    private userService: UserService,
    private userGroupService: UserGroupService,
    private impersonationService: ImpersonationService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.displayedColumns = [
      'firstName',
      'lastName',
      'email',
      'groups',
      'agencyName',
      'createdAt',
      'options',
    ];

    this.dataSource = new MatTableDataSource([] as FullUser[]);
    this.itemsPerPage = environment.itemsPerPage;
    this.page = 0;
  }

  ngAfterViewInit(): void {
    // Loading starts here and not in ngOnInit: `this.sort` only exists once the
    // view is up, and firing both means two /users/page requests per opening.
    this.dataSource.sort = this.sort;
    this.loadUsers();
    this.loadAgencyGroups();
    this.probeImpersonation();
  }

  ngOnDestroy(): void {
    clearPossibleSubscriptions(
      this.userSubscription,
      this.userUpdateSubscription
    );
  }

  private loadAgencyGroups(): void {
    this.userGroupService.getAvailableGroups().subscribe({
      next: (res: HttpResponse<UserGroup[]>) => {
        const allGroups = res.body || [];
        const agencyGroups = allGroups.filter((g: UserGroup) =>
          isAgencyGroup(g)
        );
        agencyGroups.forEach((g: UserGroup) => {
          this.agencyGroupsMap.set(g.name, g.name);
        });
        this.refreshImpersonableUsers();
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        console.error('Error loading agency groups:', err);
      },
    });
  }

  private probeImpersonation(): void {
    // The search rejects anything under 2 characters, so the probe has to send a real term.
    // Its results are discarded — only whether the call is allowed matters here.
    this.impersonationService.searchUsers(IMPERSONATION_PROBE_TERM, true).subscribe({
      next: () => {
        this.canImpersonate = true;
        this.cdr.markForCheck();
      },
      error: () => {
        this.canImpersonate = false;
        this.cdr.markForCheck();
      },
    });
  }

  private refreshImpersonableUsers(): void {
    this.impersonableUserIds = new Set(
      (this.dataSource?.data ?? [])
        .filter((user) => this.getAgencyName(user.groupDescriptions) !== '-')
        .map((user) => user.id)
    );
  }

  /** Only agency members can be impersonated; the backend refuses anyone else. */
  isImpersonable(user: FullUser): boolean {
    return this.canImpersonate && this.impersonableUserIds.has(user.id);
  }

  openImpersonationDialog(user: FullUser): void {
    this.dialog.open(ImpersonationConfirmDialogComponent, {
      data: { kind: 'user', user },
      autoFocus: false,
    });
  }

  private loadUsers(): void {
    this.loading = true;
    // Drop the in-flight request: while typing, a slow response must not
    // overwrite the results of a more recent one.
    clearPossibleSubscriptions(this.userSubscription);
    const sort = this.sort && this.sort.active ? this.sort.active : 'id';
    const order =
      this.sort && this.sort.direction ? this.sort.direction : 'desc';

    this.userSubscription = this.userService
      .queryFullUserPage({
        page: this.page,
        size: this.itemsPerPage,
        sort: sort,
        direction: order,
        ...this.filter,
      })
      .subscribe({
        next: (res: HttpResponse<any>) => {
          this.dataSource = new MatTableDataSource<FullUser>(res.body?.content);
          this.dataSource.sort = this.sort;
          this.nbUsers = res.body?.totalElements;
          this.refreshImpersonableUsers();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          console.error('Error loading users:', err);
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  applyFilter(filter: any) {
    this.page = 0;
    // Filtering restarts from the first page: leaving the paginator on its
    // former index would show a page number the results do not match.
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.filter = {};
    for (const key in filter) {
      // Only empty values are dropped, not falsy ones: `activated: false` is a
      // filter on its own.
      if (filter[key] !== null && filter[key] !== undefined && filter[key] !== '') {
        if (filter[key] instanceof Date) {
          this.filter[key] = dayjs(filter[key]).toISOString();
        } else if (dayjs.isDayjs(filter[key])) {
          this.filter[key] = dayjs(filter[key]).toISOString();
        } else {
          this.filter[key] = filter[key];
        }
      }
    }
    this.loadUsers();
  }

  changePage() {
    this.page = this.paginator.pageIndex;
    this.loadUsers();
  }

  sortChanged() {
    this.page = 0;
    this.paginator.pageIndex = 0;
    this.loadUsers();
  }

  onActivationToggle(user: FullUser): void {
    const newUser: User = { ...(user as any), activated: !user.activated };

    clearPossibleSubscriptions(this.userUpdateSubscription);

    this.userUpdateSubscription = this.userService.update(newUser).subscribe({
      next: (u) => {
        user.activated = u.body?.activated || false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error(err);
        if (err.status === 500) {
          alert(
            `Un problème est survenu sur le serveur est l'utilisateur n° ${
              user.id
            } n'a pas pu être ${
              user.activated ? 'désactivé' : 'activé'
            } :\n\n` +
              `${err.message}\n\n` +
              `Merci de contacter l'administrateur.`
          );
        }
        this.cdr.markForCheck();
      },
    });
  }

  openUserEditDialog(userId: string): void {
    clearPossibleSubscriptions(this.userUpdateSubscription);

    // Load full user by ID for editing
    this.userUpdateSubscription = this.userService.get(userId).subscribe({
      next: (res: HttpResponse<User>) => {
        const fullUser = res.body;
        const dialogRef = this.dialog.open(UserEditComponent, {
          disableClose: false,
          data: { user: fullUser },
          width: '70vw',
          height: '70vh',
        });

        clearPossibleSubscriptions(this.userUpdateSubscription);

        this.userUpdateSubscription = dialogRef.afterClosed().subscribe({
          next: (result: any) => {
            if (result) {
              if (result === -1) {
                this.snackBar.openFromComponent(PulpoSnackbarComponent, {
                  data: {
                    title: "Modification de l'utilisateur",
                    message: "L'utilisateur  a bien été supprimé.",
                  },
                  duration: 4000,
                  panelClass: ['snackbar'],
                });
              } else {
                this.snackBar.openFromComponent(PulpoSnackbarComponent, {
                  data: {
                    title: "Modification de l'utilisateur",
                    message:
                      "L'utilisateur " +
                      result.id +
                      ' a bien été modifié' +
                      '.',
                  },
                  duration: 4000,
                  panelClass: ['snackbar'],
                });
              }
            }
            this.loadUsers();
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error(err);
            this.cdr.markForCheck();
          },
        });
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  getFormattedGroups(
    groupDescriptions: Map<string, string[]> | undefined
  ): { name: string; permissions: string[] }[] {
    return this.getFilteredGroups(groupDescriptions);
  }

  getPermissionsTooltip(permissions: string[]): string {
    if (!permissions || permissions.length === 0) {
      return 'Aucune permission';
    }
    return permissions.join('\n');
  }

  toggleGroupExpanded(userId: string, groupName: string): void {
    if (!this.expandedGroups.has(userId)) {
      this.expandedGroups.set(userId, new Set());
    }
    const userGroups = this.expandedGroups.get(userId)!;
    if (userGroups.has(groupName)) {
      userGroups.delete(groupName);
    } else {
      userGroups.add(groupName);
    }
  }

  isGroupExpanded(userId: string, groupName: string): boolean {
    return this.expandedGroups.get(userId)?.has(groupName) ?? false;
  }

  /**
   * Extract primary agency name from user's groups where companyInfos has a reference (agencyCode)
   */
  getAgencyName(groupDescriptions: Map<string, string[]> | undefined): string {
    if (!groupDescriptions) {
      return '-';
    }
    const groups = Object.keys(groupDescriptions);
    for (const groupName of groups) {
      if (this.agencyGroupsMap.has(groupName)) {
        return this.agencyGroupsMap.get(groupName) || '-';
      }
    }
    return '-';
  }

  /**
   * Filter out agency groups from display (groups with agencyCode)
   * Returns only non-agency groups for the groups column
   */
  getFilteredGroups(
    groupDescriptions: Map<string, string[]> | undefined
  ): { name: string; permissions: string[] }[] {
    if (!groupDescriptions || Object.keys(groupDescriptions).length === 0) {
      return [];
    }
    const groups: { name: string; permissions: string[] }[] = [];
    for (const [groupName, permissions] of Object.entries(groupDescriptions)) {
      groups.push({
        name: groupName,
        permissions: Array.isArray(permissions) ? permissions : [],
      });
    }
    // Note: Filtering will be done once we have full group metadata
    return groups;
  }
}
